import Sheet from "@/components/Sheet"
import TermsHeader from "@/components/TermsHeader"
import TermList from "@/components/TermsList"
import { calculateStartAt } from "@/components/offerings"
import { formatAmount, formatPeriod } from "@/lib/formats"
import { defaultPrice, Offering } from "@/models/Offering"
import { Price } from "@/models/Price"
import { Provider } from "@/models/Provider"
import { RenderedTerm } from "@/models/renderedTerm"
import { cn } from "@/util/cn"
import { DateTime } from "luxon"
import { useState } from "react"
import ProviderCardSimple from "./ProviderCardSimple"
import IconCalendar from "@/components/icons/svg/IconCalendar"

const baseButtonStyles =
  "rounded-[10px] px-[20px] py-[13px] text-[15px] font-[600] leading-[22px] tracking-[-0.41px] antialiased text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none min-h-12"

function calculateIfTooLateToSubscribe(
  price: Price,
  localTimeOverride: DateTime | undefined,
) {
  const { cohortDate, period } = price

  const startAt = calculateStartAt(price, localTimeOverride)

  if (!cohortDate || !startAt || !period) {
    return false
  }

  return (
    startAt.startOf("day") <
    (localTimeOverride ?? DateTime.now()).startOf("day")
  )
}

function ExtraSpace() {
  // Extra space provided by flex gap on parent
  return <div />
}

function OfferingTermsSheet({
  offering,
  renderedTerms,
  provider,
  onClose,
  isOpen,
}: {
  offering: Offering
  provider: Provider
  isOpen: boolean
  renderedTerms: RenderedTerm[]
  onClose: () => void
}) {
  return (
    <Sheet onClose={() => onClose()} isOpen={isOpen}>
      <main
        className={"flex flex-1 flex-col gap-[15px] px-[20px] pb-[30px]"}
        data-testid={isOpen ? "offering-terms-sheet" : null}
      >
        <TermsHeader />
        <ProviderCardSimple provider={provider} />
        <ExtraSpace />
        <TermList renderedTerms={renderedTerms} />
      </main>
    </Sheet>
  )
}

function TermsSummary({
  renderedTerms,
  openTermsSheet,
  setIsTermsAgreed,
}: {
  renderedTerms: RenderedTerm[]
  setIsTermsAgreed: (value: boolean) => void
  openTermsSheet: () => void
}) {
  const maxTermsToSummarize = 3

  const numOfTruncatedTerms = Math.max(
    0,
    renderedTerms.length - maxTermsToSummarize,
  )

  return (
    <>
      <hr className={"my-[11px] border-[#e3e3e3]"} />
      <div className={"flex flex-row items-center gap-4 pl-[2px]"}>
        <input
          onChange={(event) => setIsTermsAgreed(event.target.checked)}
          className={
            "h-5 w-5 rounded-[3px] border-[2px] border-[#999] text-[#4349FE] checked:border-[#4349FE] focus:shadow-none focus:outline-none focus:ring-transparent"
          }
          type={"checkbox"}
        />
        <div className={"flex items-center"}>
          <p
            data-testid={"terms-summary-text"}
            className={
              "text-[13px] font-[400] leading-[18px] text-[#89888D] antialiased"
            }
          >
            {"Agree to terms: ".concat(
              renderedTerms
                .slice(0, Math.min(maxTermsToSummarize, renderedTerms.length))
                .map(({ summary }, i) => {
                  // Keep first work captialized. Lowercase the rest
                  return summary
                    .split(" ")
                    .map((s, j) => (j === 0 && i === 0 ? s : s.toLowerCase()))
                    .join(" ")
                })
                .join(", "),
            )}
            {numOfTruncatedTerms ? (
              <>
                &nbsp;
                <a
                  data-testid={"more-terms-button"}
                  className={
                    "inline whitespace-nowrap font-normal leading-[18px] text-[#4447F3] underline"
                  }
                  onClick={openTermsSheet}
                >
                  + {numOfTruncatedTerms} more.
                </a>
              </>
            ) : (
              <>
                .&nbsp;
                <button
                  data-testid={"view-terms-button"}
                  type={"button"}
                  className={"inline leading-[18px] text-[#4447F3] underline"}
                  onClick={openTermsSheet}
                >
                  View
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </>
  )
}

export default function OfferingPageContent({
  offering,
  renderedTerms,
  provider,
  localTimeOverride,
  onClick,
}: {
  offering: Offering
  provider: Provider
  renderedTerms?: RenderedTerm[]
  localTimeOverride?: DateTime
  onClick: (isFuture?: boolean) => void
}) {
  const price = defaultPrice(offering)
  const startAt = calculateStartAt(price, localTimeOverride)
  // console.log({ price, startAt })

  const startAtFormatted = startAt?.toLocaleString({
    weekday: "short",
    day: "numeric",
    month: "short",
  })
  const isTooLateToSubscribe = calculateIfTooLateToSubscribe(
    price,
    localTimeOverride,
  )

  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false)

  const [isTermsAgreed, setIsTermsAgreed] = useState(false)

  const isFuture = !!startAt

  const purchaseActionNode = () => {
    if (offering.type === "SUBSCRIPTION" && price.type !== "INSTALLMENT") {
      return "Subscribe"
    } else {
      return "Buy"
    }
  }

  const isSubButtonDisabled = renderedTerms && !isTermsAgreed

  return (
    <>
      {renderedTerms && (
        <OfferingTermsSheet
          offering={offering}
          provider={provider}
          renderedTerms={renderedTerms}
          isOpen={isTermsSheetOpen}
          onClose={() => {
            setIsTermsSheetOpen(false)
          }}
        />
      )}
      <div
        data-testid={offering.offeringID}
        className="flex w-full max-w-[600px] flex-col gap-5 rounded-[10px] border border-[#c3c3c3] bg-white p-[16px]"
      >
        <div className={"flex w-full flex-col gap-[6px]"}>
          <h1
            className={
              "text-[20px] font-bold leading-[25px] tracking-[-0.35px] text-[#000] antialiased"
            }
          >
            {offering.name}
          </h1>
          <h2
            data-testid={"PRICE_LINE"}
            className={
              "text-[17px] font-semibold leading-[22px] tracking-[-0.43px] text-[#4349FE] antialiased"
            }
          >
            {formatAmount(price.unitAmount, price.currency) ?? "0.00"}{" "}
            {formatPeriod(price) ?? "per period"}
          </h2>
          {startAt && (
            <div
              className={
                "flex flex-row items-center gap-x-[10px] leading-[18px]"
              }
            >
              <IconCalendar className={"h-[15px] w-[17px] text-brand-blue"} />
              <span
                className={
                  "text-[11pt] font-[400] leading-[18px] text-black antialiased"
                }
              >{`Billing starts ${startAtFormatted}`}</span>
            </div>
          )}
          {price.introductoryPricing?.setupFee.amount && (
            <div
              className={
                "whitespace-pre-wrap text-[11pt] font-[400] leading-[18px] text-black antialiased"
              }
            >
              Plus{" "}
              {formatAmount(
                price.introductoryPricing.setupFee.amount,
                price.currency,
              )}{" "}
              setup fee{startAt ? " paid today" : ""}
            </div>
          )}
          {offering.description && (
            <div className="whitespace-pre-wrap text-[11pt] font-normal leading-[15pt] text-[#89888D] antialiased">
              {offering.description}
            </div>
          )}

          {renderedTerms && (
            <TermsSummary
              setIsTermsAgreed={setIsTermsAgreed}
              renderedTerms={renderedTerms}
              openTermsSheet={() => setIsTermsSheetOpen(true)}
            />
          )}
        </div>
        <div className={"flex w-full flex-col gap-[10px]"}>
          {(offering.status !== "active" && (
            <button
              type="button"
              data-testid={"offering-card-button"}
              disabled={true}
              className={`${baseButtonStyles} w-full bg-[#999999]`}
            >
              Currently Unavailable
            </button>
          )) ||
            (isTooLateToSubscribe && (
              <button
                type="button"
                data-testid={"offering-card-button"}
                disabled={true}
                className={`${baseButtonStyles} w-full bg-[#999999]`}
              >
                Past Start Date
              </button>
            )) || (
              <button
                type="button"
                data-testid={"offering-card-button"}
                onClick={() => {
                  onClick(isFuture)
                }}
                disabled={isSubButtonDisabled}
                className={cn(
                  baseButtonStyles,
                  isSubButtonDisabled ? "bg-[#999999]" : "bg-brand-blue-alt",
                  "w-full hover:bg-indigo-500 focus-visible:outline-indigo-600",
                )}
              >
                {purchaseActionNode()}
              </button>
            )}
        </div>
      </div>
    </>
  )
}
